import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDossiers, getUsers, getStatutsConfig, bulkDossiers, createDossier, getProspects, getSettings, linkProspectToDossier } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Search, MapPin, User, HardHat, Euro, ArrowUpRight, Users as UsersIcon, Edit3, Plus, Link2, UserPlus } from 'lucide-react';
import { UserBadge, CoverThumb, fuzzyMatch } from '../components/DocumentPreview';
import { BulkActionBar, SelectCheckbox } from '../components/BulkActionBar';
import { SearchableSelect } from '../components/SearchableSelect';
import { toast } from 'sonner';

const STATUT_LABELS = {
    nouveau_prospect: 'Nouveau',
    a_qualifier: 'À qualifier',
    rdv_a_planifier: 'RDV à planifier',
    rdv_planifie: 'RDV planifié',
    devis_a_faire: 'Devis à faire',
    devis_envoye: 'Devis envoyé',
    relance_a_faire: 'Relance',
    relance: 'Relance',
    devis_accepte: 'Accepté',
    accepte: 'Accepté',
    devis_refuse: 'Refusé',
    refuse: 'Refusé',
    chantier_planifie: 'Chantier planifié',
    chantier_en_cours: 'Chantier en cours',
    chantier_termine: 'Chantier terminé',
    reception_effectuee: 'Réception',
    satisfaction_envoyee: 'Satisfaction',
    avis_google_demande: 'Avis demandé',
    parrainage_obtenu: 'Parrainé',
    dossier_clos: 'Clos'
};

const statusColor = (statut) => {
    if (!statut) return 'bg-[#F4F4F5] text-[#52525B]';
    if (statut.includes('refuse')) return 'bg-red-100 text-red-700';
    if (statut.includes('termine') || statut.includes('clos') || statut.includes('accepte') || statut.includes('reception')) return 'bg-green-100 text-green-700';
    if (statut.includes('chantier')) return 'bg-blue-100 text-blue-700';
    if (statut.includes('relance')) return 'bg-orange-100 text-orange-700';
    return 'bg-[#E85A24]/10 text-[#E85A24]';
};

export const ProjetsPage = () => {
    const { user } = useAuth();
    const [dossiers, setDossiers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statutFilter, setStatutFilter] = useState('all');
    const [commercialFilter, setCommercialFilter] = useState('all');
    const [selectedIds, setSelectedIds] = useState([]);
    const [statutsConfig, setStatutsConfig] = useState({});
    const [prospects, setProspects] = useState([]);
    const [typesTravaux, setTypesTravaux] = useState([]);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [linkingDossierId, setLinkingDossierId] = useState(null);
    const [newProject, setNewProject] = useState({ titre: '', description: '', type_travaux: '', montant_estime: '', prospect_id: '' });
    const [creating, setCreating] = useState(false);
    const [linkProspectId, setLinkProspectId] = useState('');
    const isAdmin = user?.role === 'admin' || user?.role === 'manager';

    const refresh = async () => {
        try {
            const [dossiersRes, usersRes, statutsRes, prospectsRes, settingsRes] = await Promise.all([
                getDossiers(),
                isAdmin ? getUsers().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                getStatutsConfig().catch(() => ({ data: { labels: {} } })),
                getProspects().catch(() => ({ data: [] })),
                getSettings().catch(() => ({ data: {} }))
            ]);
            setDossiers(dossiersRes.data || []);
            setUsers(usersRes.data || []);
            setStatutsConfig(statutsRes.data?.labels || {});
            setProspects(prospectsRes.data || []);
            setTypesTravaux(settingsRes.data?.types_travaux || []);
        } catch (e) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, [isAdmin]);

    // Refresh prospects when user comes back from another tab (e.g. after creating a client)
    useEffect(() => {
        const onFocus = () => {
            getProspects().then((r) => setProspects(r.data || [])).catch(() => {});
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);
    const handleCreateProject = async () => {
        if (!newProject.titre.trim()) {
            toast.error('Le titre du projet est obligatoire');
            return;
        }
        setCreating(true);
        try {
            const payload = {
                titre: newProject.titre,
                description: newProject.description || undefined,
                type_travaux: newProject.type_travaux || undefined,
                montant_estime: newProject.montant_estime ? Number(newProject.montant_estime) : undefined,
                prospect_id: newProject.prospect_id || undefined
            };
            await createDossier(payload);
            toast.success(
                newProject.prospect_id
                    ? 'Projet créé et rattaché au client'
                    : 'Projet créé — vous pourrez rattacher un client plus tard'
            );
            setShowCreateDialog(false);
            setNewProject({ titre: '', description: '', type_travaux: '', montant_estime: '', prospect_id: '' });
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de création');
        } finally {
            setCreating(false);
        }
    };

    const handleLinkProspect = async () => {
        if (!linkProspectId) {
            toast.error('Sélectionnez un client');
            return;
        }
        try {
            await linkProspectToDossier(linkingDossierId, linkProspectId);
            toast.success('Client rattaché au projet');
            setLinkingDossierId(null);
            setLinkProspectId('');
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const handleBulkApply = async ({ actionId, payload }) => {
        const fieldMap = { assign: 'commercial_id', status: 'statut' };
        const field = fieldMap[actionId];
        if (!field) throw new Error('Action inconnue');
        await bulkDossiers('update', selectedIds, { [field]: payload[field] });
        setSelectedIds([]);
        await refresh();
    };

    const handleBulkDelete = async () => {
        await bulkDossiers('delete', selectedIds);
        setSelectedIds([]);
        await refresh();
    };

    const toggleSelect = (id, checked) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
    };

    const toggleSelectAll = (checked) => {
        setSelectedIds(checked ? filteredDossiers.map(d => d.id) : []);
    };

    const filteredDossiers = dossiers.filter(d => {
        if (statutFilter !== 'all' && d.statut !== statutFilter) return false;
        if (commercialFilter !== 'all' && d.commercial_id !== commercialFilter) return false;
        if (search) {
            const prospect = d.prospect || {};
            const haystack = [
                d.titre, d.type_travaux, d.commercial_nom,
                prospect.nom, prospect.prenom, prospect.societe, prospect.telephone, prospect.email,
                prospect.adresse, prospect.ville, prospect.code_postal
            ].filter(Boolean).join(' ');
            if (!fuzzyMatch(haystack, search)) return false;
        }
        return true;
    });

    const statutOptions = Array.from(new Set(dossiers.map(d => d.statut))).filter(Boolean);

    // Searchable options for prospect selection (fuzzy by name, email, phone, company)
    const prospectOptions = prospects.map((p) => ({
        value: p.id,
        label: `${p.prenom || ''} ${p.nom || ''}`.trim() || '—',
        sublabel: [p.societe, p.ville, p.email, p.telephone].filter(Boolean).join(' · '),
        keywords: `${p.email || ''} ${p.telephone || ''} ${p.societe || ''} ${p.ville || ''} ${p.code_postal || ''}`,
    }));

    const openNewProspectTab = () => {
        // Open prospect creation in new tab so user keeps current dialog state
        window.open('/prospects/nouveau', '_blank', 'noopener,noreferrer');
    };

    const commercialName = (cid) => {
        const u = users.find(x => x.id === cid);
        return u?.name || '—';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="projets-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#0A0A0A] tracking-tight flex items-center gap-3">
                        <HardHat className="w-8 h-8 text-[#E85A24]" />
                        Projets
                    </h1>
                    <p className="text-[#52525B] mt-1">Vue par projet — {filteredDossiers.length} dossier(s)</p>
                </div>
                <Button
                    onClick={() => setShowCreateDialog(true)}
                    className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm h-10"
                    data-testid="new-project-btn"
                >
                    <Plus className="w-4 h-4 mr-2" /> Nouveau projet
                </Button>
            </div>

            {/* Filters */}
            <div className="bg-white border border-[#E4E4E7] p-4 rounded-sm flex flex-wrap gap-3">
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                    <Input
                        placeholder="Rechercher (titre, client, adresse...)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 h-10 rounded-sm"
                        data-testid="projet-search-input"
                    />
                </div>
                <Select value={statutFilter} onValueChange={setStatutFilter}>
                    <SelectTrigger className="w-[200px] h-10 rounded-sm" data-testid="projet-statut-filter">
                        <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous les statuts</SelectItem>
                        {statutOptions.map(s => <SelectItem key={s} value={s}>{STATUT_LABELS[s] || s}</SelectItem>)}
                    </SelectContent>
                </Select>
                {isAdmin && users.length > 0 && (
                    <Select value={commercialFilter} onValueChange={setCommercialFilter}>
                        <SelectTrigger className="w-[200px] h-10 rounded-sm" data-testid="projet-commercial-filter">
                            <SelectValue placeholder="Tous les commerciaux" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les commerciaux</SelectItem>
                            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Bulk action bar (admin/manager only) */}
            {isAdmin && (
                <BulkActionBar
                    selectedIds={selectedIds}
                    entityLabel="projet(s)"
                    onClear={() => setSelectedIds([])}
                    onApply={handleBulkApply}
                    onDelete={handleBulkDelete}
                    actions={[
                        {
                            id: 'assign',
                            label: 'Réassigner',
                            icon: <UsersIcon className="w-4 h-4 mr-1" />,
                            dialogFields: [{
                                name: 'commercial_id',
                                label: 'Commercial',
                                type: 'select',
                                required: true,
                                options: users.map(u => ({ value: u.id, label: u.name }))
                            }]
                        },
                        {
                            id: 'status',
                            label: 'Changer statut',
                            icon: <Edit3 className="w-4 h-4 mr-1" />,
                            dialogFields: [{
                                name: 'statut',
                                label: 'Statut',
                                type: 'select',
                                required: true,
                                options: Object.entries(statutsConfig).map(([k, v]) => ({ value: k, label: v }))
                            }]
                        }
                    ]}
                />
            )}

            {/* Projects Table (desktop) / cards (mobile) */}
            <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
                {filteredDossiers.length === 0 ? (
                    <div className="p-12 text-center text-[#52525B]">
                        <HardHat className="w-12 h-12 mx-auto text-[#E4E4E7] mb-3" />
                        <p>Aucun projet ne correspond aux filtres</p>
                    </div>
                ) : (
                    <>
                    {/* Mobile cards */}
                    <div className="md:hidden divide-y divide-[#E4E4E7]">
                        {filteredDossiers.map((d) => {
                            const prospect = d.prospect || {};
                            const addr = [prospect.adresse, prospect.code_postal, prospect.ville].filter(Boolean).join(', ');
                            return (
                                <div key={d.id} className="p-3" data-testid={`projet-card-${d.id}`}>
                                    <div className="flex items-start gap-3">
                                        {isAdmin && (
                                            <div className="pt-1">
                                                <SelectCheckbox
                                                    checked={selectedIds.includes(d.id)}
                                                    onChange={(checked) => toggleSelect(d.id, checked)}
                                                    dataTestid={`select-projet-${d.id}`}
                                                />
                                            </div>
                                        )}
                                        <CoverThumb dossierId={d.id} hasPhoto={d.has_cover_photo} size={56} />
                                        <div className="flex-1 min-w-0">
                                            <Link to={`/dossiers/${d.id}`} className="font-semibold text-[#0A0A0A] hover:text-[#E85A24] text-sm">
                                                {d.titre || '—'}
                                            </Link>
                                            {d.type_travaux && <p className="text-xs text-[#52525B]">{d.type_travaux}</p>}
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#52525B] mt-1">
                                                {(prospect.nom || prospect.prenom) ? (
                                                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {prospect.prenom || ''} {prospect.nom || ''}</span>
                                                ) : isAdmin ? (
                                                    <button
                                                        onClick={() => { setLinkingDossierId(d.id); setLinkProspectId(''); }}
                                                        className="text-[#E85A24] underline"
                                                        data-testid={`link-prospect-btn-${d.id}`}
                                                    >Rattacher client</button>
                                                ) : <span className="italic">À rattacher</span>}
                                                {addr && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {addr}</span>}
                                                {d.commercial_id && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {d.commercial_nom || commercialName(d.commercial_id)}</span>}
                                                {d.montant_devis ? <span className="flex items-center gap-1 font-semibold text-[#0A0A0A]"><Euro className="w-3 h-3" /> {Number(d.montant_devis).toLocaleString('fr-FR')} HT</span> : null}
                                            </div>
                                            <div className="mt-2 flex items-center justify-between">
                                                <span className={`inline-flex px-2 py-0.5 rounded-sm text-[10px] font-medium ${statusColor(d.statut)}`}>
                                                    {STATUT_LABELS[d.statut] || d.statut}
                                                </span>
                                                <Link to={`/dossiers/${d.id}`} className="text-[#E85A24] text-xs font-medium flex items-center gap-1">
                                                    Ouvrir <ArrowUpRight className="w-3 h-3" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {isAdmin && (
                                    <TableHead className="w-10">
                                        <SelectCheckbox
                                            checked={selectedIds.length > 0 && selectedIds.length === filteredDossiers.length}
                                            onChange={toggleSelectAll}
                                            dataTestid="select-all-projets"
                                        />
                                    </TableHead>
                                )}
                                <TableHead>Projet</TableHead>
                                <TableHead>Client</TableHead>
                                <TableHead>Adresse</TableHead>
                                <TableHead>Commercial</TableHead>
                                <TableHead>Montant devis (€ HT)</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredDossiers.map(d => {
                                const prospect = d.prospect || {};
                                const addr = [prospect.adresse, prospect.code_postal, prospect.ville].filter(Boolean).join(', ');
                                return (
                                    <TableRow key={d.id} data-testid={`projet-row-${d.id}`} className="hover:bg-[#FAFAFA]">
                                        {isAdmin && (
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <SelectCheckbox
                                                    checked={selectedIds.includes(d.id)}
                                                    onChange={(checked) => toggleSelect(d.id, checked)}
                                                    dataTestid={`select-projet-${d.id}`}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <div className="flex items-start gap-3">
                                                <CoverThumb dossierId={d.id} hasPhoto={d.has_cover_photo} size={48} />
                                                <div className="min-w-0">
                                                    <Link to={`/dossiers/${d.id}`} className="font-semibold text-[#0A0A0A] hover:text-[#E85A24]">
                                                        {d.titre || '—'}
                                                    </Link>
                                                    {d.type_travaux && (
                                                        <p className="text-xs text-[#52525B] mt-0.5">{d.type_travaux}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {(prospect && (prospect.nom || prospect.prenom)) ? (
                                                <>
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-[#52525B]" />
                                                        <span>{prospect.prenom || ''} {prospect.nom || '—'}</span>
                                                    </div>
                                                    {prospect.societe && <p className="text-xs text-[#52525B] pl-6">{prospect.societe}</p>}
                                                </>
                                            ) : (
                                                isAdmin ? (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={(e) => { e.stopPropagation(); setLinkingDossierId(d.id); setLinkProspectId(''); }}
                                                        className="rounded-sm text-[#E85A24] border-[#E85A24] hover:bg-[#E85A24] hover:text-white"
                                                        data-testid={`link-prospect-btn-${d.id}`}
                                                    >
                                                        <Link2 className="w-3.5 h-3.5 mr-1" /> Rattacher un client
                                                    </Button>
                                                ) : (
                                                    <span className="text-[#52525B] text-sm italic">À rattacher</span>
                                                )
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {addr ? (
                                                <div className="flex items-start gap-2 text-sm">
                                                    <MapPin className="w-4 h-4 text-[#52525B] mt-0.5 shrink-0" />
                                                    <span>{addr}</span>
                                                </div>
                                            ) : <span className="text-[#52525B] text-sm">—</span>}
                                        </TableCell>
                                        <TableCell>
                                            {d.commercial_id ? (
                                                <UserBadge userId={d.commercial_id} name={d.commercial_nom || commercialName(d.commercial_id)} size="sm" />
                                            ) : <span className="text-[#52525B] text-sm">—</span>}
                                        </TableCell>
                                        <TableCell className="text-sm font-semibold">
                                            {d.montant_devis ? (
                                                <span className="flex items-center gap-1">
                                                    <Euro className="w-3.5 h-3.5" />
                                                    {Number(d.montant_devis).toLocaleString('fr-FR')} HT
                                                </span>
                                            ) : <span className="text-[#52525B]">—</span>}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`inline-flex px-2 py-1 rounded-sm text-xs font-medium ${statusColor(d.statut)}`}>
                                                {STATUT_LABELS[d.statut] || d.statut}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={`/dossiers/${d.id}`} className="text-[#E85A24] hover:underline text-sm flex items-center gap-1">
                                                Ouvrir <ArrowUpRight className="w-3.5 h-3.5" />
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </div>
                    </>
                )}
            </div>

            {/* Create Project Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="max-w-xl" data-testid="new-project-dialog">
                    <DialogHeader>
                        <DialogTitle>Nouveau projet</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="field-label">Titre du projet *</Label>
                            <Input
                                value={newProject.titre}
                                onChange={(e) => setNewProject({ ...newProject, titre: e.target.value })}
                                placeholder="Ex: Rénovation cuisine appartement Paris 15"
                                className="h-10 rounded-sm mt-1"
                                data-testid="new-project-titre"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="field-label">Type de travaux</Label>
                                <Select value={newProject.type_travaux} onValueChange={(v) => setNewProject({ ...newProject, type_travaux: v })}>
                                    <SelectTrigger className="h-10 rounded-sm mt-1" data-testid="new-project-type">
                                        <SelectValue placeholder="Sélectionner..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {typesTravaux.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="field-label">Montant estimé (€ HT)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={newProject.montant_estime}
                                    onChange={(e) => setNewProject({ ...newProject, montant_estime: e.target.value })}
                                    className="h-10 rounded-sm mt-1"
                                    data-testid="new-project-montant"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="field-label">Client (optionnel — à rattacher plus tard si vide)</Label>
                            <div className="flex gap-2 mt-1">
                                <div className="flex-1 min-w-0">
                                    <SearchableSelect
                                        options={prospectOptions}
                                        value={newProject.prospect_id}
                                        onChange={(v) => setNewProject({ ...newProject, prospect_id: v })}
                                        placeholder="Rechercher un client (nom, société, téléphone...)"
                                        emptyMsg="Aucun client trouvé"
                                        testId="new-project-prospect"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={openNewProspectTab}
                                    className="rounded-sm h-11 border-[#E85A24] text-[#E85A24] hover:bg-[#E85A24] hover:text-white shrink-0"
                                    data-testid="new-project-create-client-btn"
                                    title="Créer un nouveau client (nouvel onglet)"
                                >
                                    <UserPlus className="w-4 h-4 mr-1" /> Créer un client
                                </Button>
                            </div>
                            <p className="text-xs text-[#52525B] mt-1">Astuce : créez le client dans le nouvel onglet, revenez ici, puis sélectionnez-le.</p>
                        </div>
                        <div>
                            <Label className="field-label">Description</Label>
                            <Textarea
                                rows={3}
                                value={newProject.description}
                                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                                className="rounded-sm mt-1"
                                placeholder="Détails du projet..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
                        <Button
                            onClick={handleCreateProject}
                            disabled={creating || !newProject.titre.trim()}
                            className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white"
                            data-testid="create-project-submit"
                        >
                            {creating ? 'Création...' : 'Créer le projet'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Link Prospect to existing orphan dossier Dialog */}
            <Dialog open={!!linkingDossierId} onOpenChange={(o) => !o && setLinkingDossierId(null)}>
                <DialogContent data-testid="link-prospect-dialog">
                    <DialogHeader>
                        <DialogTitle>Rattacher un client au projet</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label className="field-label">Client</Label>
                        <div className="flex gap-2">
                            <div className="flex-1 min-w-0">
                                <SearchableSelect
                                    options={prospectOptions}
                                    value={linkProspectId}
                                    onChange={setLinkProspectId}
                                    placeholder="Rechercher un client..."
                                    emptyMsg="Aucun client trouvé"
                                    testId="link-prospect-select"
                                />
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={openNewProspectTab}
                                className="rounded-sm h-11 border-[#E85A24] text-[#E85A24] hover:bg-[#E85A24] hover:text-white shrink-0"
                                data-testid="link-prospect-create-btn"
                                title="Créer un nouveau client (nouvel onglet)"
                            >
                                <UserPlus className="w-4 h-4 mr-1" /> Créer
                            </Button>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLinkingDossierId(null)}>Annuler</Button>
                        <Button
                            onClick={handleLinkProspect}
                            disabled={!linkProspectId}
                            className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white"
                            data-testid="link-prospect-confirm"
                        >
                            Rattacher
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
